import { Injectable, NgZone, OnDestroy } from '@angular/core';
import maplibregl from 'maplibre-gl';
import { Subscription } from 'rxjs';
import { CatchHeatService, CatchHeatState } from './catch-heat.service';
import {
  CATCH_HEAT_MIN_ZOOM, GeoFeature, HOTSPOT_RING_FALLBACK_NM, SEARCH_ZONE_NM,
  circlePolygon, visibleCatchPins,
} from '../shared/utils/catch-heat.util';

const SRC = {
  density: 'catch-heat-density-src',
  rings: 'catch-heat-rings-src',
  recs: 'catch-heat-recs-src',
  zone: 'catch-heat-zone-src',
  pins: 'catch-heat-pins-src',
} as const;

const LAYER = {
  density: 'catch-heat-density',
  ringFill: 'catch-heat-ring-fill',
  ringLine: 'catch-heat-ring-line',
  zone: 'catch-heat-zone',
  recDot: 'catch-heat-rec-dot',
  recLabel: 'catch-heat-rec-label',
  pins: 'catch-heat-pins',
} as const;

const AMBER = '#FF8F00';
/** Own-boat layer: heat is drawn beneath it so the vessel stays on top of the blur. */
const ABOVE_LAYER_ID = 'vessel-unclustered-layer-v3';
/** Nearest-N hotspots used to frame the camera: a fleet-wide fetch spans disconnected regions. */
const SNAP_NEAREST = 15;

const empty = (features: GeoFeature[] = []) => ({ type: 'FeatureCollection' as const, features });

const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

/**
 * Draws the catch-heat stack on the MapLibre map: density blur, hotspot footprint rings, ranked
 * "next best spots", the #1 spot's dashed search zone and today's live catch pins — a port of
 * Flutter's `_initCatchHeatLayer` / `_syncCatchHeat` (`home_screen_overlay_sync.dart`).
 *
 * Owns only the map layers; data and selection live in `CatchHeatService`. Pins are circles here,
 * not the waypoint marker pipeline Flutter reuses.
 */
@Injectable({ providedIn: 'root' })
export class CatchHeatMapService implements OnDestroy {
  private map: maplibregl.Map | null = null;
  private sub?: Subscription;
  private popup: maplibregl.Popup | null = null;
  private wasEnabled = false;
  private snapArmed = false;
  private handlersBound = false;

  constructor(private heat: CatchHeatService, private ngZone: NgZone) {}

  /** Call once the style is loaded; call again (`restyle`) after any `setStyle`. */
  attach(map: maplibregl.Map): void {
    // Layer-scoped handlers survive `setStyle`, so they are bound once per map instance.
    if (this.map !== map) this.handlersBound = false;
    this.map = map;
    this.ensureLayers();
    this.bindInteractions();
    this.sub?.unsubscribe();
    this.sub = this.heat.state$.subscribe((s) => this.ngZone.runOutsideAngular(() => this.render(s)));
  }

  /** A style swap drops every source/layer — rebuild them and repaint the current state. */
  restyle(): void {
    if (!this.map) return;
    this.ensureLayers();
    this.render(this.heat.state);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.popup?.remove();
  }

  // ── Layers ──────────────────────────────────────────────────────────────────

  private ensureLayers(): void {
    const map = this.map;
    if (!map || !map.isStyleLoaded()) return;
    const before = map.getLayer(ABOVE_LAYER_ID) ? ABOVE_LAYER_ID : undefined;
    (Object.values(SRC) as string[]).forEach((id) => {
      if (!map.getSource(id)) map.addSource(id, { type: 'geojson', data: empty() });
    });

    // Blur first so it sits UNDER the rings, spots and pins.
    if (!map.getLayer(LAYER.density)) {
      map.addLayer({
        id: LAYER.density, type: 'heatmap', source: SRC.density, minzoom: CATCH_HEAT_MIN_ZOOM,
        paint: {
          // 0→0 anchors the weight curve so fractional weights fade instead of clamping to w=1.
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'w'], 0, 0, 1, 0.6, 5, 1],
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 6, 0.9, 12, 1.5],
          // ~1.2×/zoom so a hotspot stays one coherent blob without smearing across the screen.
          'heatmap-radius': ['interpolate', ['exponential', 2], ['zoom'], 6, 14, 9, 24, 12, 48, 15, 90],
          'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'],
            0, 'rgba(11,22,34,0)', 0.1, '#2b3a8c', 0.3, '#2196c8', 0.5, '#28c86e', 0.7, '#e6d43c', 1, '#e5484d'],
          // Near-opaque so a bright ocean overlay below can't wash the heat out.
          'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0, 6.5, 0.95],
        },
      }, before);
    }
    // Invisible fill so a tap anywhere inside a footprint opens the hotspot card.
    if (!map.getLayer(LAYER.ringFill)) {
      map.addLayer({
        id: LAYER.ringFill, type: 'fill', source: SRC.rings, minzoom: 6,
        paint: { 'fill-color': AMBER, 'fill-opacity': 0.01 },
      }, before);
    }
    if (!map.getLayer(LAYER.ringLine)) {
      map.addLayer({
        id: LAYER.ringLine, type: 'line', source: SRC.rings, minzoom: 6,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': AMBER, 'line-width': 2, 'line-opacity': 0.85 },
      }, before);
    }
    if (!map.getLayer(LAYER.zone)) {
      map.addLayer({
        id: LAYER.zone, type: 'line', source: SRC.zone, minzoom: 6,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        // Dashed: "search here", not a hard boundary.
        paint: { 'line-color': AMBER, 'line-width': 2, 'line-dasharray': [2, 2] },
      }, before);
    }
    if (!map.getLayer(LAYER.recDot)) {
      map.addLayer({
        id: LAYER.recDot, type: 'circle', source: SRC.recs, minzoom: 6,
        paint: { 'circle-radius': 6, 'circle-color': AMBER, 'circle-opacity': 0.95, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1.5 },
      }, before);
    }
    if (!map.getLayer(LAYER.recLabel)) {
      map.addLayer({
        id: LAYER.recLabel, type: 'symbol', source: SRC.recs, minzoom: 6,
        layout: {
          'text-field': ['get', 'label'], 'text-size': 14, 'text-font': ['Open Sans Bold'],
          'text-anchor': 'bottom-left', 'text-offset': [0.9, -0.9], 'text-allow-overlap': true, 'text-ignore-placement': true,
        },
        paint: { 'text-color': '#5D3B00', 'text-halo-color': '#fff', 'text-halo-width': 1.4 },
      }, before);
    }
    if (!map.getLayer(LAYER.pins)) {
      map.addLayer({
        id: LAYER.pins, type: 'circle', source: SRC.pins,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 5, 12, 9],
          'circle-color': '#0A84FF', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2,
        },
      }, before);
    }
  }

  // ── Rendering ───────────────────────────────────────────────────────────────

  private render(s: CatchHeatState): void {
    const map = this.map;
    if (!map || !map.isStyleLoaded()) return;
    this.ensureLayers();
    const on = s.enabled;
    const set = (id: string, features: GeoFeature[]) => (map.getSource(id) as maplibregl.GeoJSONSource | undefined)?.setData(empty(features) as any);
    const d = s.data;

    set(SRC.density, on ? d.density : []);
    set(SRC.rings, on ? d.hotspots.flatMap((f) => this.ringFeature(f)) : []);
    set(SRC.recs, on ? d.recommended.map((r) => ({
      type: 'Feature', geometry: { type: 'Point', coordinates: [r.lon, r.lat] },
      properties: { label: r.rank == null ? '' : `#${r.rank}`, summary: r.summary ?? '', band: r.band ?? '', confidence: r.confidence ?? null },
    })) : []);
    // recommended is rank-ordered, so the first row is the primary spot.
    const top = d.recommended[0];
    set(SRC.zone, on && top ? [{ type: 'Feature', geometry: circlePolygon(top.lat, top.lon, SEARCH_ZONE_NM), properties: {} }] : []);
    set(SRC.pins, on ? visibleCatchPins(d.pins, s.species) : []);

    if (on && !this.wasEnabled) this.snapArmed = true;
    this.wasEnabled = on;
    if (!on) {
      this.popup?.remove();
      this.snapArmed = false;
    } else if (this.snapArmed && s.status !== 'loading') {
      this.snapArmed = false;
      this.snapCamera(s);
    }
  }

  private ringFeature(h: GeoFeature): GeoFeature[] {
    const c = h.geometry?.coordinates;
    if (!Array.isArray(c) || c.length < 2) return [];
    const nm = typeof h.properties?.['footprint_nm'] === 'number' ? h.properties['footprint_nm'] : HOTSPOT_RING_FALLBACK_NM;
    const { summary, band, confidence } = h.properties ?? {};
    return [{ type: 'Feature', geometry: circlePolygon(c[1], c[0], nm), properties: { summary: summary ?? '', band: band ?? '', confidence: confidence ?? null } }];
  }

  /** One-shot on turn-on: frame the nearest hotspots (else the raw catches), never on a chip change. */
  private snapCamera(s: CatchHeatState): void {
    const map = this.map;
    if (!map) return;
    const feats = s.data.hotspots.length ? s.data.hotspots : s.data.density;
    const centre = map.getCenter();
    const pts = feats
      .map((f) => f.geometry?.coordinates as number[] | undefined)
      .filter((c): c is number[] => Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1]))
      .sort((a, b) => Math.hypot(a[0] - centre.lng, a[1] - centre.lat) - Math.hypot(b[0] - centre.lng, b[1] - centre.lat))
      .slice(0, SNAP_NEAREST);
    if (!pts.length) return;
    const bounds = pts.reduce((b, p) => b.extend(p as [number, number]), new maplibregl.LngLatBounds(pts[0] as [number, number], pts[0] as [number, number]));
    map.fitBounds(bounds, { padding: 80, maxZoom: 11, duration: 900 });
  }

  // ── Taps ────────────────────────────────────────────────────────────────────

  private bindInteractions(): void {
    const map = this.map;
    if (!map || this.handlersBound) return;
    this.handlersBound = true;
    const pointer = (layer: string) => {
      map.on('mouseenter', layer, () => (map.getCanvas().style.cursor = 'pointer'));
      map.on('mouseleave', layer, () => (map.getCanvas().style.cursor = ''));
    };
    // Pins/spots sit above the footprint fill, so they must win the tap.
    map.on('click', LAYER.pins, (e) => this.showPin(e));
    map.on('click', LAYER.recDot, (e) => this.showSpot(e, 'Next best spot'));
    map.on('click', LAYER.ringFill, (e) => {
      if (map.queryRenderedFeatures(e.point, { layers: [LAYER.pins, LAYER.recDot] }).length) return;
      this.showSpot(e, 'Catch hotspot');
    });
    [LAYER.pins, LAYER.recDot, LAYER.ringFill].forEach(pointer);
  }

  private open(e: maplibregl.MapMouseEvent, html: string): void {
    const map = this.map;
    if (!map) return;
    this.popup?.remove();
    this.popup = new maplibregl.Popup({ closeButton: true, maxWidth: '320px', offset: 10 })
      .setLngLat(e.lngLat).setHTML(`<div style="font:14px/1.45 -apple-system,'Segoe UI',Roboto,sans-serif;color:#1c1c1e;padding:4px 2px">${html}</div>`)
      .addTo(map);
  }

  private showSpot(e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }, title: string): void {
    const p = e.features?.[0]?.properties ?? {};
    const summary = String(p['summary'] ?? '');
    const conf = typeof p['confidence'] === 'number' ? p['confidence'] : null;
    if (!summary && conf == null) return;
    const band = String(p['band'] ?? '');
    this.open(e, `
      <div style="font-size:12px;color:#8e8e93;margin-bottom:6px">${esc(title)}</div>
      ${summary ? `<div style="font-size:15px">${esc(summary)}</div>` : ''}
      ${conf != null ? `<div style="font-size:12px;color:#8e8e93;margin-top:6px">Confidence: ${esc(band)}${band ? ' ' : ''}(${Math.round(conf * 100)}%)</div>` : ''}`);
  }

  private showPin(e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }): void {
    const p = e.features?.[0]?.properties ?? {};
    // GeoJSON nests objects as JSON strings once they pass through the map's tile worker.
    const author = typeof p['author'] === 'string' ? safeJson(p['author']) : p['author'];
    const ts = p['ts'] ? new Date(String(p['ts'])) : null;
    const when = ts && !isNaN(ts.getTime()) ? ts.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '';
    this.open(e, `
      <div style="font-size:12px;color:#8e8e93;margin-bottom:6px">Catch</div>
      <div style="font-size:15px;font-weight:600">${esc(p['subtype'] || 'Catch')}${p['qty'] ? ` × ${esc(p['qty'])}` : ''}</div>
      ${author?.name ? `<div style="margin-top:4px">${esc(author.name)}</div>` : ''}
      ${when ? `<div style="font-size:12px;color:#8e8e93;margin-top:4px">${esc(when)}</div>` : ''}`);
  }
}

function safeJson(s: string): any {
  try { return JSON.parse(s); } catch { return null; }
}
